import { useLazyQuery } from "@apollo/client";
import {
  GET_GIS_INTAKE_FORM_DATA_NONAUTH_QUERY,
  GET_GIS_INTAKE_FORM_DATA_QUERY,
  GisIntakeFormDataResponse,
  IntakeFormData,
} from "@app/service/intakeForm";
import { useCallback, useEffect, useState } from "react";

interface GisIntakeFormDataNonAuthResponse {
  getGisIntakeFormDataNonAuth: IntakeFormData;
}

/**
 * Return type for the useGisIntakeFormData hook
 */
export interface UseGisIntakeFormDataResult {
  /** The pre-filled intake form data (null if not yet fetched) */
  intakeFormData: IntakeFormData | null;
  /** Whether the data is currently loading */
  isLoading: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** Manually trigger the data fetch */
  fetchIntakeFormData: (orderUidPublic: string, testUid: string) => void;
}
/**
 * Custom hook to fetch pre-filled GIS intake form data from the backend.
 * This replaces the need to manually aggregate data from multiple sources
 * and separately check for GIS appointments.
 *
 * @param orderUidPublic - The public order UID (optional, auto-fetches when provided)
 * @param testUid - The test UID (optional, auto-fetches when provided)
 * @param token - Optional signed guest token. When provided, the prefill is
 *   fetched via the NonAuth resolver (anonymous order-lookup funnel) instead of
 *   the authenticated one.
 * @returns Object containing intake form data, loading state, error, and manual trigger
 */
export function useGisIntakeFormData(
  orderUidPublic?: string | null,
  testUid?: string | null,
  token?: string | null
): UseGisIntakeFormDataResult {
  const [intakeFormData, setIntakeFormData] = useState<IntakeFormData | null>(
    null
  );

  const [executeAuthQuery, authResult] =
    useLazyQuery<GisIntakeFormDataResponse>(GET_GIS_INTAKE_FORM_DATA_QUERY, {
      fetchPolicy: "network-only",
    });
  const [executeGuestQuery, guestResult] =
    useLazyQuery<GisIntakeFormDataNonAuthResponse>(
      GET_GIS_INTAKE_FORM_DATA_NONAUTH_QUERY,
      { fetchPolicy: "network-only" }
    );

  const loading = authResult.loading || guestResult.loading;
  const error = authResult.error ?? guestResult.error;

  // Manual trigger function
  const fetchIntakeFormData = useCallback(
    (orderUid: string, testId: string) => {
      if (token) {
        executeGuestQuery({
          variables: { token, orderUidPublic: orderUid, testUid: testId },
        });
        return;
      }
      executeAuthQuery({
        variables: { orderUidPublic: orderUid, testUid: testId },
      });
    },
    [executeAuthQuery, executeGuestQuery, token]
  );

  // Refetch whenever order/test changes (lazy query "called" flag would block
  // subsequent fetches on the same hook instance after the first run).
  useEffect(() => {
    if (!orderUidPublic || !testUid) {
      setIntakeFormData(null);
      return;
    }
    setIntakeFormData(null);
    fetchIntakeFormData(orderUidPublic, testUid);
  }, [orderUidPublic, testUid, fetchIntakeFormData]);

  // Update state when data is received (from whichever resolver ran)
  useEffect(() => {
    const next =
      guestResult.data?.getGisIntakeFormDataNonAuth ??
      authResult.data?.getGisIntakeFormData;
    if (next) {
      setIntakeFormData(next);
    }
  }, [authResult.data, guestResult.data]);

  // Log errors
  useEffect(() => {
    if (error) {
      console.error("GIS intake form data fetch ERROR:", error);
    }
  }, [error]);

  return {
    intakeFormData,
    isLoading: loading,
    error: error?.message ?? null,
    fetchIntakeFormData,
  };
}
